// CPC Staking hub — wallet-app style delegate / overview

import { useSuiClientQuery } from '@mysten/dapp-kit';
import { type SuiValidatorSummary } from '@mysten/sui.js/client';
import { Text } from '@mysten/ui';
import { useCallback, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { PageLayout } from '~/components/Layout/PageLayout';
import { CpcPageHero, CpcSection, CpcSectionTitle, cpcPageContentClass } from '~/components/cpc/CpcPageHero';
import { StakeModal } from '~/components/StakeModal';
import { SelectValidatorModal, type ValidatorOption } from '~/components/SelectValidator';
import { CpcEpochProgressBar } from '~/components/stake/CpcEpochProgressBar';
import { CpcStakePositionsList } from '~/components/stake/CpcStakePositionsList';
import { CpcValidatorsSection } from '~/components/stake/CpcValidatorsSection';
import { useCpcHomeStats } from '~/hooks/useCpcHomeStats';
import { useCpcStakeActions } from '~/hooks/useCpcStakeActions';
import { useCpcStakeFlow } from '~/hooks/useCpcStakeFlow';
import { useCpcStakePositions, type StakePositionView } from '~/hooks/useCpcStakePositions';
import { useCpcValidatorsApy, type CpcValidatorApyEntry } from '~/hooks/useCpcValidatorsApy';
import { useT } from '~/i18n/LocaleContext';
import { CPC_SYMBOL } from '~/utils/cpcCoin';
import { formatEpochCountdown, formatMistAsDgc } from '~/utils/explorerFormat';
import { sortValidatorsByStake } from '~/utils/sortValidatorsByStake';

function StatCard({
	label,
	value,
	sub,
	valueClassName = '!text-white',
}: {
	label: string;
	value: React.ReactNode;
	sub?: string;
	/** Tailwind text color classes for the main value (use ! prefix on dark pages). */
	valueClassName?: string;
}) {
	return (
		<div className="rounded-xl border border-white/10 bg-white/5 p-4">
			<p className="text-xs font-medium text-[#aeb9d0]">{label}</p>
			<div className={`mt-2 text-2xl font-bold tabular-nums ${valueClassName}`}>{value}</div>
			{sub ? <p className="mt-1 text-xs text-[#94a3b8]">{sub}</p> : null}
		</div>
	);
}

function validatorToOption(
	v: SuiValidatorSummary,
	apyMap: Record<string, CpcValidatorApyEntry> | null,
): ValidatorOption {
	return {
		address: v.suiAddress,
		name: v.name,
		imageUrl: v.imageUrl,
		stake: v.stakingPoolSuiBalance,
		apy: apyMap?.[v.suiAddress]?.apy ?? null,
		apyEstimated: apyMap?.[v.suiAddress]?.isEstimated,
		commission: Number(v.commissionRate) / 100,
	};
}

export default function CpcStakePage() {
	const t = useT();
	const flow = useCpcStakeFlow();
	const homeStats = useCpcHomeStats();
	const { hash } = useLocation();
	const address = flow.account?.address ?? '';

	const {
		data: positions,
		isPending: positionsPending,
		refetch: refetchPositions,
	} = useCpcStakePositions(address);
	const stakeActions = useCpcStakeActions(address);

	const { data: systemState, isPending: systemPending } = useSuiClientQuery(
		'getLatestSuiSystemState',
	);
	const validators = systemState?.activeValidators ?? [];
	const { apyMap } = useCpcValidatorsApy(validators);

	const myTotalStake = useMemo(() => {
		if (!positions?.length) return 0n;
		return positions.reduce((acc, p) => {
			const cpc = parseFloat(p.principalCpc);
			if (!Number.isFinite(cpc)) return acc;
			return acc + BigInt(Math.round(cpc * 1e9));
		}, 0n);
	}, [positions]);

	const validatorByAddress = useMemo(() => {
		const map = new Map<string, SuiValidatorSummary>();
		for (const v of validators) map.set(v.suiAddress.toLowerCase(), v);
		return map;
	}, [validators]);

	const epochLabel = systemState?.epoch ?? homeStats.epoch ?? '—';
	const epochDurationMs = systemState?.epochDurationMs
		? Number(systemState.epochDurationMs)
		: null;

	const validatorOptionsForModal: ValidatorOption[] = useMemo(
		() => sortValidatorsByStake(validators).map((v) => validatorToOption(v, apyMap)),
		[validators, apyMap],
	);

	const handleClaim = useCallback(
		async (pos: StakePositionView) => {
			if (pos.kind === 'fungible_staked_sui' && pos.fungibleStakedSuiId) {
				await stakeActions.claimFromFungible(pos.fungibleStakedSuiId);
			} else if (pos.stakedSuiId) {
				await stakeActions.claimFromStakedSui(pos.stakedSuiId);
			}
			refetchPositions();
		},
		[refetchPositions, stakeActions],
	);

	const handleRedeem = useCallback(
		async (pos: StakePositionView) => {
			if (pos.kind === 'fungible_staked_sui' && pos.fungibleStakedSuiId) {
				await stakeActions.redeemFungible(pos.fungibleStakedSuiId);
			} else if (pos.stakedSuiId) {
				await stakeActions.redeemFromStakedSui(pos.stakedSuiId);
			}
			refetchPositions();
		},
		[refetchPositions, stakeActions],
	);

	const handleExecuteStake = useCallback(
		async (validatorAddress: string) => {
			await flow.executeStake(validatorAddress);
			refetchPositions();
		},
		[flow, refetchPositions],
	);

	useEffect(() => {
		if (hash === '#validators') {
			requestAnimationFrame(() => {
				document.getElementById('validators')?.scrollIntoView({ behavior: 'smooth' });
			});
		}
	}, [hash]);

	return (
		<PageLayout
			gradient={{
				size: 'md',
				content: (
					<CpcPageHero
						title={t('stake.pageTitle')}
						subtitle={t('stake.pageSubtitle')}
					/>
				),
			}}
			content={
				<div className={`${cpcPageContentClass} pb-6 text-[#e2e8f0] md:pb-8`}>
						<CpcValidatorsSection />

						<CpcSection>
							<CpcSectionTitle>{t('stake.epochProgress')}</CpcSectionTitle>
							<div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
								<StatCard
									label={t('stake.currentEpoch')}
									value={systemPending ? '…' : epochLabel}
									sub={
										epochDurationMs
											? t('stake.epochCycleAbout').replace(
													'{duration}',
													formatEpochCountdown(epochDurationMs),
												)
											: undefined
									}
									valueClassName="!text-white"
								/>
								<StatCard
									label={t('stake.latestCheckpoint')}
									value={
										homeStats.checkpointSeq != null
											? BigInt(homeStats.checkpointSeq).toLocaleString()
											: '—'
									}
									sub={t('stake.checkpointHint')}
									valueClassName="!text-white"
								/>
							</div>
							<CpcEpochProgressBar className="mt-4" />
						</CpcSection>

						<CpcSection>
							<CpcSectionTitle>{t('stake.myStakes')}</CpcSectionTitle>
							{!flow.account ? (
								<div className="flex flex-col items-center gap-3 py-8 text-center">
									<Text variant="body/medium" color="dark">
										{t('stake.connectHint')}
									</Text>
								</div>
							) : (
								<>
									<div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
										<div className="rounded-xl bg-[#0F172A]/60 border border-[#1E293B] p-4">
											<p className="text-xs font-medium text-[#aeb9d0]">{t('common.available')}</p>
											<div className="mt-1 text-xl font-bold text-white tabular-nums">
												{flow.availableCpc} {CPC_SYMBOL}
											</div>
										</div>
										<div className="rounded-xl bg-[#0F172A]/60 border border-[#1E293B] p-4">
											<p className="text-xs font-medium text-[#aeb9d0]">{t('stake.staked')}</p>
											<div className="mt-1 text-xl font-bold tabular-nums text-[#4ade80]">
												{positionsPending ? '…' : formatMistAsDgc(myTotalStake)}{' '}
												<span className="text-[#86efac]">{CPC_SYMBOL}</span>
											</div>
										</div>
										<div className="rounded-xl bg-[#0F172A]/60 border border-[#1E293B] p-4 flex flex-col justify-center">
											<button
												type="button"
												onClick={flow.openStakeModal}
												disabled={flow.isStaking}
												className="w-full h-11 rounded-xl bg-[#38BDF8] text-sm font-bold text-white hover:bg-[#38BDF8]/90 disabled:opacity-50"
											>
												{flow.isStaking ? t('common.submitting') : t('stake.stakeDgc')}
											</button>
											<Link
												to={`/address/${address}`}
												className="mt-2 text-center text-xs text-[#38BDF8] hover:underline"
											>
												{t('stake.viewAccount')}
											</Link>
										</div>
									</div>

									<h3 className="mb-3 text-sm font-medium text-[#f8fafc]">{t('stake.delegationDetails')}</h3>
									{positionsPending ? (
										<Text variant="body/medium" color="dark">
											{t('common.loading')}
										</Text>
									) : (
										<CpcStakePositionsList
											positions={positions ?? []}
											validatorByAddress={validatorByAddress}
											isBusy={stakeActions.isPending || flow.isStaking}
											onClaim={handleClaim}
											onRedeem={handleRedeem}
										/>
									)}
								</>
							)}
						</CpcSection>

						<section className="space-y-1 text-xs text-[#94a3b8]">
							<p>{t('stake.tipGas')}</p>
							<p>{t('stake.tipUnlock')}</p>
							<p>{t('stake.tipLeaderboard')}</p>
						</section>

					<StakeModal
						isOpen={flow.isStakeOpen}
						onClose={() => flow.setIsStakeOpen(false)}
						balance={flow.availableCpc}
						stakeableBalance={flow.stakeableCpc}
						symbol={CPC_SYMBOL}
						onConfirm={(amount, addrs) =>
							flow.handleStakeModalConfirm(
								amount,
								addrs.map((a) => {
									const found = validatorOptionsForModal.find((o) => o.address === a);
									return found ?? { address: a };
								}),
							)
						}
					/>
					<SelectValidatorModal
						isOpen={flow.isValidatorOpen}
						validators={
							flow.validatorOptions.length ? flow.validatorOptions : validatorOptionsForModal
						}
						onClose={() => flow.setIsValidatorOpen(false)}
						onSelect={handleExecuteStake}
					/>
				</div>
			}
		/>
	);
}
